import { useState, useEffect } from "react";
import axios from "axios";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid
} from "recharts";
import {
  Euro, CalendarCheck, BedDouble, Building2,
  TrendingUp, TrendingDown, RefreshCw, AlertTriangle,
  Activity, CircleDot, Clock
} from "lucide-react";

const API = "https://api.komorotel.com/api/superadmin";
const KMF_PER_EUR = 491.96775;
const toKMF = (eur) => Math.round(eur * KMF_PER_EUR).toLocaleString("fr-FR");

const timeAgo = (date) => {
  const s = Math.floor((Date.now() - new Date(date)) / 1000);
  if (s < 60) return "à l'instant";
  const m = Math.floor(s / 60);
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `il y a ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `il y a ${d} j`;
  return new Date(date).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
};

function Trend({ value }) {
  if (value == null) return null;
  const up = value >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${up ? "text-emerald-600" : "text-red-500"}`}>
      <Icon size={13} />
      {up ? "+" : ""}{value}%
    </span>
  );
}

function KpiCard({ icon: Icon, label, value, sub, trend }) {
  return (
    <div className="bg-white border border-[#e5e7eb] rounded-2xl p-5 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="w-9 h-9 rounded-xl bg-gray-50 flex items-center justify-center">
          <Icon className="text-gray-500" size={16} />
        </div>
        {trend != null && <Trend value={trend} />}
      </div>
      <div>
        <p className="text-xs text-gray-400 mb-0.5">{label}</p>
        <p className="text-2xl font-semibold tracking-tight text-gray-900">{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

const FEED_DOT = {
  reservation: "text-blue-500",
  hotel: "text-[#3A9D23]",
  request: "text-orange-500",
  admin: "text-violet-500",
};

export default function SuperAdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/dashboard`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [refreshKey]);

  const k = data?.kpis;

  return (
    <div className="w-full min-h-screen bg-[#f9fafb] p-6 flex flex-col gap-6">

      {/* ── HEADER ── */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">Dashboard</h1>
          <p className="text-sm text-gray-400 mt-0.5">
            Vue d'ensemble de la plateforme — commission {data?.commissionRate ?? 8}%
          </p>
        </div>
        <button
          onClick={() => setRefreshKey(x => x + 1)}
          className="w-9 h-9 bg-white border border-[#e5e7eb] rounded-xl flex items-center justify-center hover:bg-gray-50 transition text-gray-400"
        >
          <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* ── KPI STRIP ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          icon={Euro}
          label="Revenus plateforme"
          value={loading ? "…" : `${(k?.platformRevenue?.total ?? 0).toLocaleString("fr-FR")} €`}
          sub={k ? `${(k.platformRevenue.thisMonth).toLocaleString("fr-FR")} € ce mois · ${toKMF(k.platformRevenue.total)} KMF` : ""}
          trend={k?.platformRevenue?.trend}
        />
        <KpiCard
          icon={CalendarCheck}
          label="Réservations actives"
          value={loading ? "…" : k?.activeReservations?.total ?? 0}
          sub={k ? `${k.activeReservations.thisMonth} ce mois` : ""}
          trend={k?.activeReservations?.trend}
        />
        <KpiCard
          icon={BedDouble}
          label="ADR moyen"
          value={loading ? "…" : `${(k?.adr?.value ?? 0).toLocaleString("fr-FR")} €`}
          sub={k ? `${k.adr.thisMonth.toLocaleString("fr-FR")} € ce mois` : ""}
          trend={k?.adr?.trend}
        />
        <KpiCard
          icon={Building2}
          label="Hôtels actifs"
          value={loading ? "…" : k?.activeHotels?.total ?? 0}
          sub="Visibles sur le site public"
        />
      </div>

      {/* ── ALERTES ── */}
      {data?.alerts?.length > 0 && (
        <div className="bg-white border border-[#e5e7eb] rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="text-orange-500" size={15} />
            <p className="text-sm font-medium text-gray-900">Alertes ({data.alerts.length})</p>
          </div>
          <div className="flex flex-col gap-1.5">
            {data.alerts.map((a, i) => (
              <div key={i} className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs ${
                a.severity === "orange" ? "bg-orange-50 text-orange-700" : "bg-blue-50 text-blue-700"
              }`}>
                <CircleDot size={11} className="shrink-0" />
                <span className="truncate">{a.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── GRAPHIQUE + FEED ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Revenus mensuels */}
        <div className="lg:col-span-2 bg-white border border-[#e5e7eb] rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-medium text-gray-900">Revenus mensuels</p>
            <div className="flex items-center gap-4 text-xs text-gray-400">
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm bg-[#3A9D23]" /> Commission Komorotel
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm bg-[#d6ecc9]" /> Volume brut
              </span>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.monthlyRevenue || []} barGap={2}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false}
                       tickFormatter={(v) => `${v.toLocaleString("fr-FR")} €`} width={70} />
                <Tooltip
                  cursor={{ fill: "#f9fafb" }}
                  contentStyle={{ borderRadius: 12, border: "1px solid #e5e7eb", fontSize: 12, boxShadow: "none" }}
                  formatter={(v, name) => [`${v.toLocaleString("fr-FR")} €`, name === "commission" ? "Commission" : "Volume brut"]}
                />
                <Bar dataKey="gross" fill="#d6ecc9" radius={[4, 4, 0, 0]} />
                <Bar dataKey="commission" fill="#3A9D23" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Feed d'activité */}
        <div className="bg-white border border-[#e5e7eb] rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Activity className="text-gray-500" size={15} />
            <p className="text-sm font-medium text-gray-900">Activité récente</p>
          </div>
          {loading ? (
            <p className="text-xs text-gray-400">Chargement...</p>
          ) : data?.feed?.length === 0 ? (
            <p className="text-xs text-gray-400">Aucune activité pour le moment.</p>
          ) : (
            <div className="flex flex-col max-h-[340px] overflow-y-auto pr-1">
              {data?.feed?.map((item, i) => (
                <div key={i} className="flex items-start gap-2.5 py-2.5 border-b border-gray-50 last:border-0">
                  <CircleDot size={12} className={`mt-0.5 shrink-0 ${FEED_DOT[item.type] || "text-gray-400"}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-gray-700 leading-snug">{item.label}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5 flex items-center gap-1">
                      <Clock size={9} /> {timeAgo(item.date)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
